import { useMemo } from "react";
import { TellescopeFormFieldBaseProps } from "../../types";
import { InputTableRow } from "./InputTableRow";
import { TableInputCell } from "@tellescope/types-models";
import { cn } from "@tellescope/utils";
import { Label } from "@tellescope/design-system";
import { Button } from "@tellescope/ui";
import { useTranslation } from "react-i18next";
import { FieldDescription } from "../../FieldDescription";

export interface InputTableFieldProps extends TellescopeFormFieldBaseProps<"Input Table"> {}

export const InputTableField = ({
  id,
  title,
  description,
  htmlDescription,
  value,
  onChange,
  className,
  options,
  required,
  autoFocus,
}: InputTableFieldProps) => {
  const { t } = useTranslation("forms");
  const columnsConfig = options?.tableChoices;
  const emptyRow = useMemo<TableInputCell[]>(() => {
    return (
      columnsConfig?.map((columnConfig) => ({
        label: columnConfig.label,
        entry: "",
      })) ?? []
    );
  }, [columnsConfig]);

  if (!columnsConfig?.length) {
    return <p className="text-sm text-muted-foreground">{t("No input choices available")}</p>;
  }

  // A required table always shows at least one row to fill in; an optional one may be left empty.
  const rows = value?.length ? value : required ? [emptyRow] : [];
  const removeDisabled = required && rows.length === 1;

  return (
    <div className="space-y-1">
      {!!title && <Label required={required}>{title}</Label>}
      <FieldDescription description={description} htmlDescription={htmlDescription} />
      <div className={cn(`flex flex-col gap-1`, className)}>
        {rows.map((row, i) => (
          <InputTableRow
            key={`input-table-row-${title ?? ""}-${i}`}
            fieldId={id}
            value={row}
            columnsConfig={columnsConfig}
            minDateOffsetMs={options?.minDateOffsetMs}
            maxDateOffsetMs={options?.maxDateOffsetMs}
            onChange={(row) => onChange([...rows.slice(0, i), row, ...rows.slice(i + 1)])}
            onRemove={() => onChange([...rows.slice(0, i), ...rows.slice(i + 1)])}
            removeDisabled={removeDisabled}
            autoFocus={autoFocus && i === 0}
          />
        ))}
        <div className="flex justify-end mt-1">
          <Button
            variant="outline"
            onClick={() => onChange([...rows, emptyRow])}
            autoFocus={autoFocus && rows.length === 0}
          >
            {t("Add New Entry")}
          </Button>
        </div>
      </div>
    </div>
  );
};
